import { useState, useEffect } from 'react';
import { Link, useParams, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Globe, Menu, X, Building2, Leaf, Landmark, Building, GitMerge } from 'lucide-react';

const LOGO_URL = "/images/hg-logo.png";

const locales = [
  { code: 'en-in', label: 'India', flag: '🇮🇳' },
  { code: 'en-us', label: 'United States', flag: '🇺🇸' },
  { code: 'en-gb', label: 'United Kingdom', flag: '🇬🇧' },
  { code: 'en-nl', label: 'Netherlands', flag: '🇳🇱' },
  { code: 'en-ae', label: 'UAE', flag: '🇦🇪' },
];

const services = [
  { 
    title: 'Pharma Engineering & PMC', 
    description: 'cGMP facility expansion, HVAC, utilities, audits',
    path: '/services/pharma-engineering',
    icon: Building2
  },
  { 
    title: 'Sustainability & ESG / BRSR', 
    description: 'SEBI BRSR reporting and ESG strategy',
    path: '/services/sustainability',
    icon: Leaf
  },
  { 
    title: 'Finance & Loans', 
    description: 'Capital structuring, audits, loan facilitation',
    path: '/services/finance-loans',
    icon: Landmark
  },
  { 
    title: 'Capital Markets', 
    description: 'Investment sales, debt & equity advisory',
    path: '/services/capital-markets',
    icon: Building
  },
  { 
    title: 'Mergers & Acquisitions', 
    description: 'Full transaction lifecycle advisory',
    path: '/services/mergers-acquisitions',
    icon: GitMerge
  },
];

const Navbar = () => {
  const { locale } = useParams();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [localeOpen, setLocaleOpen] = useState(false);

  const currentLocale = locales.find(l => l.code === locale) || locales[0];

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    setServicesOpen(false);
  }, [location.pathname]);

  const navLinks = [
    { label: 'Home', path: `/${locale}` },
    { label: 'About', path: `/${locale}/about` },
    { label: 'Services', path: `/${locale}/services`, hasDropdown: true },
    { label: 'Insights', path: `/${locale}/insights` },
    { label: 'Case Studies', path: `/${locale}/case-studies` },
    { label: 'Careers', path: `/${locale}/careers` },
    { label: 'Contact', path: `/${locale}/contact` },
  ];

  return (
    <nav 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-white shadow-lg' : 'bg-white/95 backdrop-blur-sm'
      }`}
      data-testid="main-navbar"
    >
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to={`/${locale}`} className="flex-shrink-0" data-testid="nav-logo">
            <img 
              src={LOGO_URL} 
              alt="H&G - Horizon & Greenprint" 
              className="h-12 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-8">
            {navLinks.map((link) => (
              <div key={link.label} className="relative">
                {link.hasDropdown ? (
                  <div 
                    className="relative"
                    onMouseEnter={() => setServicesOpen(true)}
                    onMouseLeave={() => setServicesOpen(false)}
                  >
                    <button 
                      className="nav-link flex items-center text-navy font-medium hover:text-teal transition-colors"
                      data-testid="nav-services-dropdown"
                    >
                      {link.label}
                      <ChevronDown className={`ml-1 w-4 h-4 transition-transform ${servicesOpen ? 'rotate-180' : ''}`} />
                    </button>
                    
                    <AnimatePresence>
                      {servicesOpen && (
                        <motion.div
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          transition={{ duration: 0.2 }}
                          className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-[600px] bg-white rounded-lg shadow-2xl border border-gray-100 p-6"
                          data-testid="services-mega-menu"
                        >
                          <div className="grid grid-cols-2 gap-4">
                            {services.map((service) => {
                              const IconComponent = service.icon;
                              return (
                                <Link
                                  key={service.path}
                                  to={`/${locale}${service.path}`}
                                  className="flex items-start p-3 rounded-lg hover:bg-light-grey transition-colors group"
                                  data-testid={`nav-service-${service.path.split('/').pop()}`}
                                >
                                  <div className="flex-shrink-0 w-10 h-10 bg-teal/10 rounded-lg flex items-center justify-center mr-3 group-hover:bg-teal/20 transition-colors">
                                    <IconComponent className="w-5 h-5 text-teal" />
                                  </div>
                                  <div>
                                    <h4 className="font-semibold text-navy text-sm">{service.title}</h4>
                                    <p className="text-mid-grey text-xs mt-0.5">{service.description}</p>
                                  </div>
                                </Link>
                              );
                            })}
                          </div>
                          <div className="mt-4 pt-4 border-t border-gray-100">
                            <Link 
                              to={`/${locale}/services`}
                              className="text-teal font-medium text-sm hover:text-teal transition-colors"
                            >
                              View All Services →
                            </Link>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ) : (
                  <Link 
                    to={link.path}
                    className={`nav-link font-medium transition-colors ${
                      location.pathname === link.path ? 'text-teal' : 'text-navy hover:text-teal'
                    }`}
                    data-testid={`nav-${link.label.toLowerCase().replace(' ', '-')}`}
                  >
                    {link.label}
                  </Link>
                )}
              </div>
            ))}
          </div>

          {/* Right Side - Locale & CTA */}
          <div className="hidden lg:flex items-center space-x-4">
            {/* Locale Selector */}
            <div 
              className="relative"
              onMouseEnter={() => setLocaleOpen(true)}
              onMouseLeave={() => setLocaleOpen(false)}
            >
              <button 
                className="flex items-center text-navy hover:text-teal transition-colors"
                data-testid="locale-selector"
              >
                <Globe className="w-5 h-5 mr-2" />
                <span className="text-sm font-medium">{currentLocale.flag} {currentLocale.label}</span>
                <ChevronDown className={`ml-1 w-4 h-4 transition-transform ${localeOpen ? 'rotate-180' : ''}`} />
              </button>
              
              <AnimatePresence>
                {localeOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.2 }}
                    className="absolute top-full right-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-100 py-2"
                    data-testid="locale-dropdown"
                  >
                    {locales.map((loc) => (
                      <Link
                        key={loc.code}
                        to={location.pathname.replace(`/${locale}`, `/${loc.code}`)}
                        className={`block px-4 py-2 text-sm hover:bg-light-grey transition-colors ${
                          loc.code === locale ? 'text-teal font-medium bg-teal/5' : 'text-navy'
                        }`}
                        data-testid={`locale-${loc.code}`}
                      >
                        {loc.flag} {loc.label}
                      </Link>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* CTA Button */}
            <Link 
              to={`/${locale}/contact`}
              className="btn-primary text-sm"
              data-testid="nav-cta-button"
            >
              Request a Consultation
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button 
            className="lg:hidden text-navy"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-white border-t border-gray-100"
            data-testid="mobile-menu"
          >
            <div className="max-w-7xl mx-auto px-6 py-6 space-y-4">
              {navLinks.map((link) => (
                <div key={link.label}>
                  {link.hasDropdown ? (
                    <div>
                      <button 
                        onClick={() => setServicesOpen(!servicesOpen)}
                        className="flex items-center justify-between w-full py-2 text-navy font-medium"
                      >
                        {link.label}
                        <ChevronDown className={`w-4 h-4 transition-transform ${servicesOpen ? 'rotate-180' : ''}`} />
                      </button>
                      {servicesOpen && (
                        <div className="pl-4 space-y-2 mt-2">
                          {services.map((service) => (
                            <Link
                              key={service.path}
                              to={`/${locale}${service.path}`}
                              className="block py-2 text-mid-grey hover:text-teal transition-colors"
                            >
                              {service.title}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <Link 
                      to={link.path}
                      className="block py-2 text-navy font-medium hover:text-teal transition-colors"
                    >
                      {link.label}
                    </Link>
                  )}
                </div>
              ))}
              
              {/* Mobile Locale Selector */}
              <div className="pt-4 border-t border-gray-100">
                <p className="text-sm text-mid-grey mb-2">Select Region</p>
                <div className="grid grid-cols-2 gap-2">
                  {locales.map((loc) => (
                    <Link
                      key={loc.code}
                      to={location.pathname.replace(`/${locale}`, `/${loc.code}`)}
                      className={`px-3 py-2 text-sm rounded-lg text-center ${
                        loc.code === locale 
                          ? 'bg-teal text-white' 
                          : 'bg-light-grey text-navy hover:bg-gray-200'
                      }`}
                    >
                      {loc.flag} {loc.label}
                    </Link>
                  ))}
                </div>
              </div>
              
              {/* Mobile CTA */}
              <Link 
                to={`/${locale}/contact`}
                className="btn-primary block text-center mt-4"
              >
                Request a Consultation
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;
